/**
 * MFA Challenge Page
 *
 * After password sign-in (AAL1), admins who have a TOTP factor enrolled
 * are redirected here to complete the login MFA challenge (→ AAL2).
 *
 * Accessible at /mfa/challenge.
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export default function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAal2, hasTotpFactor, mfaFactors, challengeAndVerifyTotp } = useAuth();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Where to go after successful challenge
  const from = (location.state?.from) ?? '/';

  useEffect(() => {
    // Already at AAL2 or no factor enrolled — skip this page
    if (isAal2 || !hasTotpFactor) {
      navigate(from, { replace: true });
    }
  }, [isAal2, hasTotpFactor, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    const factorId = mfaFactors[0]?.id;
    if (!factorId) {
      toast.error('No authenticator factor found');
      setLoading(false);
      return;
    }

    const { error } = await challengeAndVerifyTotp(factorId, code);
    setLoading(false);

    if (error) {
      toast.error('Verification failed', { description: error.message });
      setCode('');
      return;
    }

    toast.success('Verified — welcome back');
    navigate(from, { replace: true });
  };

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>Two-Factor Authentication — Coach</title>
      </Helmet>
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-sm shadow-none border">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl font-semibold">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Open your authenticator app and enter the 6-digit code.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totp-code">Verification Code</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  autoFocus
                  className="text-center text-xl tracking-widest"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Verify
              </Button>
            </form>
            <p className="mt-4 text-xs text-muted-foreground text-center">
              Lost your device? Contact a project owner to remove the factor from
              the Supabase Authentication dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
